import { useDispatch } from 'react-redux'
import { AppDispatch, useRoutes } from '../../../store'
import Table from '../../common/Table'
import Button from '@mui/material/Button'
import { useAdCreativeSelector, usePageParamsFilteredSelector, useUser, withDefaultPagination } from '../../../utils'
import Wrapper from '../../common/Wrapper'
import { getConfig } from './tableConfig'
import { useState } from 'react'
import { AdCreative, AdCreativeInit, AdCreativeWithUrls, Role, type User } from 'common/api/v1/types'
import UploadDialog from './UploadDialog'
import CommonActions from './CommonActions'
import { createAdCreative, listAdCreatives } from '../../../redux/actions/adCreativeActions'
import { getLocationParams } from '../../../utils/params'
import CreateDialog from './CreateDialog'
import { MissingContent } from '../../common/MissingContent'
import { useNavigate } from 'react-router'

export const canEditAdCreative = (user: User, createdBy: AdCreative['createdBy']) => {
  return (user.role === Role.admin && user.group === createdBy) || user.role === Role.super
}

export const AdCreativeList = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [uploadTo, setUploadTo] = useState<AdCreativeWithUrls | undefined>(undefined)
  const [selected, setSelected] = useState<Array<AdCreativeWithUrls['id']>>([])
  const [openCreateDialog, setOpenCreateDialog] = useState(false)
  const navigate = useNavigate()

  const user = useUser()
  const routes = useRoutes()
  const { adCreatives, total, loading } = usePageParamsFilteredSelector(useAdCreativeSelector)

  return (
    <>
      <UploadDialog
        adCreative={uploadTo}
        onClose={() => {
          setUploadTo(undefined)
          dispatch(listAdCreatives(withDefaultPagination(getLocationParams())))
        }}
      />
      <CreateDialog
        open={openCreateDialog}
        onCreate={(adCreativeInit: AdCreativeInit) => {
          dispatch(createAdCreative(adCreativeInit)).then((res) => {
            dispatch(listAdCreatives(withDefaultPagination(getLocationParams())))
            setOpenCreateDialog(false)
            setUploadTo(res.payload as AdCreativeWithUrls)
          })
        }}
        onClose={() => {
          setOpenCreateDialog(false)
        }}
      />
      <Wrapper
        name={['Ad Insertion', 'Ad Creatives']}
        actions={
          <Button id={'button-add'} variant="contained" color="primary" onClick={() => setOpenCreateDialog(true)}>
            Add
          </Button>
        }
      >
        <CommonActions selected={selected} setSelected={setSelected} adCreatives={adCreatives} user={user} />
        <Table
          id="ad-creatives-table"
          emptyMessageComponent={
            <MissingContent message="No ad creatives" buttonText="Add" onClick={() => setOpenCreateDialog(true)} />
          }
          data={adCreatives}
          pagination={{
            total: total,
            useUrlSearchParams: true,
          }}
          config={getConfig({
            adCreatives,
            dispatch,
            onUpload: (adCreative: AdCreativeWithUrls) => {
              setUploadTo(adCreative)
            },
            selected,
            handleSelect: (id: string) => {
              setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]))
            },
            handleSelectAll: () => {
              setSelected((prev) =>
                prev.length === adCreatives.length ? [] : adCreatives.map((adCreative) => adCreative.id),
              )
            },
            user,
            routes,
          })}
          rowProps={({ id, createdBy }) => {
            const isItemSelected = Boolean(id && selected.includes(id))
            return {
              onDoubleClick: () =>
                canEditAdCreative(user, createdBy) ? void navigate(routes.adCreativesUpdate({ id })) : null,
              'aria-checked': isItemSelected,
              tabIndex: -1,
              selected: isItemSelected,
            }
          }}
          pending={loading}
        />
      </Wrapper>
    </>
  )
}
